import { selectViewedTickets, setViewedTickets } from '../redux/ticket/ticket.actions';
import { selectViewedRooms, setViewedRooms } from '../redux/room/room.actions';
import { marcaDaSala } from '../utils/marcaDaSala';
import { ehAbaUnicaDeChat, rotuloDaAbaDeChat } from '../utils/abaUnicaDeChat';
import {
  PERMISSAO_DA_PAGINA,
  caminhoEntraNoAcessoRapido,
  paginaDoAcessoRapido,
} from './paginasDoAcessoRapido';

/**
 * Configuração centralizada de todos os tipos de QuickAccess
 * Para adicionar um novo tipo, basta adicionar uma nova entrada neste array
 * 
 * @typedef {Object} QuickAccessConfig
 * @property {string} id - Identificador único do tipo
 * @property {'redux'|'localStorage'|'state'} storageType - Tipo de armazenamento
 * @property {string} [storageKey] - Chave para localStorage (se storageType === 'localStorage')
 * @property {Function} getItemLabel - Função que retorna o label do item
 * @property {Function} getItemPath - Função que retorna o path para navegação
 * @property {Function} [onItemRemove] - Função chamada ao remover item
 * @property {Function} [onItemClick] - Função customizada para clique (opcional)
 * @property {number} [maxLabelLength=30] - Comprimento máximo do label
 * @property {Object} [autoAdd] - Configuração para adicionar automaticamente
 * @property {RegExp} [autoAdd.pathPattern] - Pattern para detectar quando adicionar
 * @property {Function} [autoAdd.getItem] - Função que retorna o item a ser adicionado
 * @property {Function} [autoAdd.shouldAdd] - Função que determina se deve adicionar
 * @property {Function} [autoAdd.onAdd] - Callback após adicionar
 * 
 * @example
 * // Exemplo: Adicionar Rooms ao QuickAccess
 * // 1. Crie actions no Redux (similar a setViewedTickets)
 * // 2. Adicione a configuração abaixo:
 * {
 *   id: 'rooms',
 *   storageType: 'redux',
 *   getItemLabel: (item) => `${item.client?.name} - ${item.client?.company?.code}`,
 *   getItemPath: (item) => `/?roomId=${item.room_id}`, // ou sua rota customizada
 *   maxLabelLength: 25,
 *   reduxSelector: selectViewedRooms, // seu selector
 *   reduxAction: setViewedRooms, // sua action
 * }
 */
/**
 * Páginas do Quick Access que exigem permissão de rota (demais paths sem entrada = liberados).
 *
 * Mora em `paginasDoAcessoRapido` junto do catálogo, para a permissão não
 * ficar num arquivo e a página noutro. Reexportado aqui porque é por este nome
 * que o resto do código conhece a tabela.
 *
 * A tabela antiga tinha uma entrada `/criar-avisos-mural`, que não é página
 * nenhuma -- é a permissão de ESCREVER no Mural, dentro de `/mural-avisos`.
 * Como nenhum caminho bate com ela, ela nunca liberou nem barrou nada.
 */
export const quickAccessPagePermissions = PERMISSAO_DA_PAGINA;

export const canAccessQuickAccessPage = (path, permissions) => {
  if (!path) return false;
  const required = quickAccessPagePermissions[path];
  if (!required) return true;
  return Array.isArray(permissions) && permissions.includes(required);
};

export const filterQuickAccessPages = (pages, permissions) => {
  if (!Array.isArray(pages)) return [];
  return pages.filter((item) => canAccessQuickAccessPage(item?.path, permissions));
};

export const quickAccessConfigs = [
  {
    id: 'tickets',
    storageType: 'redux',
    // Para Redux, o componente QuickAccessTypeRedux vai usar useSelector
    getItemLabel: (item) => {
      if (item?.isDraft && item?.draftId) {
        return 'Novo ticket';
      }
      // `number` e o protocolo por tenant; `id` (PK global) so serve para
      // o caminho da rota, logo abaixo.
      const titulo = item.title || 'Sem título';
      return item?.number != null ? `#${item.number} - ${titulo}` : titulo;
    },
    getItemPath: (item) => {
      if (item?.isDraft && item?.draftId) {
        return `/ticket/new?draft=${encodeURIComponent(item.draftId)}`;
      }
      return `/ticket/${item.id}`;
    },
    maxLabelLength: 30,
    // Redux actions para gerenciar
    reduxSelector: selectViewedTickets,
    reduxAction: setViewedTickets,
  },
  {
    id: 'rooms',
    storageType: 'redux',
    getItemLabel: (item) => {
      /*
       * Desde 19/10/2026 TODAS as conversas cabem em uma aba só (pedido da
       * Gisele). O rótulo com nome do cliente e marca continua aqui porque a
       * aba agregada é construída a partir destas mesmas conversas -- e porque
       * o dia em que o produto quiser as duas formas, a regra da marca não
       * precisa ser reescrita.
       */
      if (ehAbaUnicaDeChat(item)) return rotuloDaAbaDeChat(item);

      const clientName = item.client?.name || 'Cliente';
      const companyCode = item.client?.company?.code || '';
      /*
       * A MARCA na frente: SysFar e MAX são duas marcas dentro do mesmo
       * tenant, cada uma com o seu número, e a aba não dizia por onde a
       * conversa chegou (pedido da Gisele, 18/10). O operador se apresentava
       * pela marca errada.
       *
       * Na frente, e não no fim, porque a aba trunca o texto: o fim some
       * primeiro, e identificar é justamente o que não pode sumir. Sala do
       * site não ganha prefixo -- `marcaDaSala` já devolve `null` nela.
       */
      const marca = marcaDaSala(item);
      const nome = `${clientName}${companyCode ? ` - ${companyCode}` : ''}`;

      return marca ? `${marca} · ${nome}` : nome;
    },
    getItemPath: (item) => {
      // A aba agregada abre a página de chat NORMAL, com a lista de hoje
      // (decisão do dono do produto em 19/10) -- e não uma conversa.
      if (ehAbaUnicaDeChat(item)) return '/chat';

      // Vai direto para a página de chat abrindo o room da aba (o InProgressPage
      // lê o roomId da query). Antes ia para "/" (dashboard), sem abrir o chat.
      return item.room_id ? `/chat?roomId=${item.room_id}` : '/chat';
    },
    maxLabelLength: 25,
    reduxSelector: selectViewedRooms,
    reduxAction: setViewedRooms,
  },
  // Páginas do LeftSideBar
  {
    id: 'pages',
    storageType: 'localStorage',
    storageKey: 'quickAccessPages',
    getItemLabel: (item) => item.name,
    getItemPath: (item) => item.path,
    maxLabelLength: 20,
    /*
     * Auto-add: a aba nasce quando o operador visita a página.
     *
     * As duas funções saem do MESMO catálogo
     * (`config/paginasDoAcessoRapido`). Antes eram uma expressão regular de
     * caminhos e um mapa de rótulos, escritos à mão, que tinham de concordar:
     * caminho fora da regex nunca entrava, e caminho na regex mas fora do mapa
     * fazia o `getItem` devolver `null` -- também nunca entrava, e sem erro
     * nenhum. Metade das páginas do sistema estava em um dos dois buracos
     * (ponto 30 da Gisele).
     */
    autoAdd: {
      aceitaCaminho: caminhoEntraNoAcessoRapido,
      getItem: paginaDoAcessoRapido,
      shouldAdd: (newItem, currentItems) => {
        // Só adiciona se não existir
        return !currentItems.some(item => item.path === newItem.path);
      }
    }
  },
  // Aba do Orby: não navega — abre/fecha o painel lateral do Orby (evento tratado no
  // Template). É adicionada pelo botão "fixar" dentro da janela do Orby.
  {
    id: 'orby',
    storageType: 'localStorage',
    storageKey: 'quickAccessOrby',
    getItemLabel: () => 'Orby',
    getItemPath: () => null,
    onItemClick: () => window.dispatchEvent(new CustomEvent('orbyTabClick')),
    maxLabelLength: 20,
  }
];
